/**
 * Design-system text input.
 *
 * Surfaces `id`, `label`, and `error` so forms can render an
 * accessible field with one component. The label is required by the
 * `jsx-a11y` rule set; pass `hideLabel` if a label is provided visually
 * elsewhere (e.g. the password field on the unified Get Started form).
 */
import { type InputHTMLAttributes, forwardRef, useId } from 'react';

import styles from './input.module.css';

export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> {
  label: string;
  hint?: string | undefined;
  error?: string | undefined;
  hideLabel?: boolean;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { label, hint, error, hideLabel = false, id, className, ...rest },
  ref,
) {
  const generatedId = useId();
  const inputId = id ?? generatedId;
  const hintId = `${inputId}-hint`;
  const errorId = `${inputId}-error`;

  return (
    <div className={styles.field}>
      <label
        htmlFor={inputId}
        className={[styles.label, hideLabel ? styles.srOnly : ''].filter(Boolean).join(' ')}
      >
        {label}
      </label>
      <input
        ref={ref}
        id={inputId}
        className={[styles.input, error ? styles.invalid : '', className ?? '']
          .filter(Boolean)
          .join(' ')}
        aria-invalid={error ? true : undefined}
        aria-describedby={
          [error ? errorId : null, hint ? hintId : null].filter(Boolean).join(' ') || undefined
        }
        {...rest}
      />
      {hint && !error ? (
        <span id={hintId} className={styles.hint}>
          {hint}
        </span>
      ) : null}
      {error ? (
        <span id={errorId} className={styles.error} role="alert">
          {error}
        </span>
      ) : null}
    </div>
  );
});
